'use client';

// Scroll-driven occasion "drum" (design OC-JS): the pinned list eases toward the
// scroll position; words curve, blur and fade away from the active one while the
// photos cross-fade on the same continuous index. Writes transform/opacity/filter
// only, rAF-throttled, idle when the section is off screen.
import { useEffect, useRef, type RefObject } from 'react';

type Refs = {
  section: RefObject<HTMLElement | null>;
  pin: RefObject<HTMLElement | null>;
  listWrap: RefObject<HTMLElement | null>;
  list: RefObject<HTMLElement | null>;
  frame: RefObject<HTMLElement | null>;
};

type Geo = {
  centers: number[];
  /** list-wrap y of the frame's vertical centre (desktop) or the wrap's middle (mobile) */
  mid: number;
  range: number;
  mobile: boolean;
};

const MOBILE_MAX = 900;

export function useOccasionDrum(
  refs: Refs,
  count: number,
  navh: number,
  onActive: (index: number) => void,
) {
  const onActiveRef = useRef(onActive);
  onActiveRef.current = onActive;

  // Exposed so a click can scroll to an item.
  const scrollToIndex = useRef<(i: number) => void>(() => {});

  useEffect(() => {
    const sec = refs.section.current;
    const pin = refs.pin.current;
    const lw = refs.listWrap.current;
    const l = refs.list.current;
    const f = refs.frame.current;
    if (!sec || !pin || !lw || !l || !f) return;
    let alive = true;
    // One occasion has nowhere to scroll to: index 0 is both ends.
    const last = Math.max(1, count - 1);

    const items = Array.from(l.children) as HTMLElement[];
    const layers = Array.from(f.children) as HTMLElement[];
    const itemCache = items.map(() => ['', '', '', '']);
    const layerCache = layers.map(() => ['', '', '', '']);
    let listCache = '';

    const measure = (): Geo => {
      const mobile = window.innerWidth <= MOBILE_MAX;
      const fb = f.getBoundingClientRect();
      const lb = lw.getBoundingClientRect();
      return {
        centers: items.map((e) => e.offsetTop + e.offsetHeight / 2),
        mid: mobile ? lw.clientHeight / 2 : fb.top + fb.height / 2 - lb.top,
        range: Math.max(1, sec.offsetHeight - pin.offsetHeight),
        mobile,
      };
    };
    let geo = measure();

    const target = (g: Geo) => {
      const top = sec.getBoundingClientRect().top;
      return Math.min(1, Math.max(0, (navh - top) / g.range)) * (count - 1);
    };

    const set = (
      el: HTMLElement,
      cache: string[],
      k: number,
      prop: 'transform' | 'filter' | 'opacity' | 'color' | 'zIndex',
      v: string,
    ) => {
      if (cache[k] === v) return;
      cache[k] = v;
      el.style[prop] = v;
    };

    let cur = -1;
    const paint = (g: Geo, idx: number) => {
      const n = count;
      const i0 = Math.min(n - 1, Math.floor(idx));
      const i1 = Math.min(n - 1, i0 + 1);
      const fr = idx - i0;
      const ctr = g.centers[i0] + fr * (g.centers[i1] - g.centers[i0]);
      const ty = `translate3d(0,${Math.round((g.mid - ctr) * 10) / 10}px,0)`;
      if (ty !== listCache) {
        listCache = ty;
        l.style.transform = ty;
      }

      for (let i = 0; i < n; i++) {
        const d = i - idx;
        const ad = Math.abs(d);
        const c = Math.max(-3, Math.min(3, d));
        const e = items[i];
        const cache = itemCache[i];
        const x = g.mobile ? 0 : Math.round(-c * c * 9 * 10) / 10;
        set(
          e,
          cache,
          0,
          'transform',
          `perspective(900px) translate3d(${x}px,0,0) rotateX(${(
            -c * 17
          ).toFixed(2)}deg) rotateZ(${(-c * 1.1).toFixed(2)}deg) scale(${(
            1 -
            Math.min(ad, 3) * 0.04
          ).toFixed(3)})`,
        );
        set(
          e,
          cache,
          1,
          'filter',
          ad < 0.03 ? 'none' : `blur(${(Math.min(ad, 3) * 1.5).toFixed(2)}px)`,
        );
        set(
          e,
          cache,
          2,
          'opacity',
          Math.max(0.14, 1 - Math.min(ad, 4) * 0.2).toFixed(3),
        );
        set(
          e,
          cache,
          3,
          'color',
          `rgba(0,0,0,${(0.2 + 0.8 * Math.max(0, 1 - ad)).toFixed(3)})`,
        );
      }

      // the old photo stays solid while the next fades in over it out of a light blur
      const w = Math.min(1, Math.max(0, idx - i0));
      const ew = w * w * (3 - 2 * w);
      const fe = ew * ew * ew * (ew * (ew * 6 - 15) + 10);
      layers.forEach((e, i) => {
        const prev = i === i0;
        const next = i === i0 + 1;
        let op = 0;
        let z = 0;
        let sc = 1.06;
        let fl = 'none';
        if (prev) {
          op = 1;
          z = 1;
          sc = 1 + 0.035 * fe;
          fl = `blur(${(2.2 * fe).toFixed(2)}px)`;
        } else if (next) {
          op = fe;
          z = 2;
          sc = 1.07 - 0.07 * fe;
          fl = `blur(${(10 * (1 - fe)).toFixed(2)}px)`;
        }
        const cache = layerCache[i];
        set(e, cache, 0, 'opacity', op.toFixed(3));
        set(e, cache, 1, 'zIndex', String(z));
        set(e, cache, 2, 'filter', fl);
        set(e, cache, 3, 'transform', `scale(${sc.toFixed(4)})`);
      });

      const best = Math.round(idx);
      if (best !== cur) {
        cur = best;
        onActiveRef.current(best);
      }
    };

    let raf = 0;
    let running = false;
    let val = 0;
    let tgt = 0;
    const tick = () => {
      tgt = target(geo);
      val += (tgt - val) * 0.075;
      if (Math.abs(tgt - val) < 0.002) val = tgt;
      paint(geo, val);
      if (val !== tgt) {
        raf = requestAnimationFrame(tick);
      } else {
        running = false;
      }
    };
    const kick = () => {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(tick);
    };

    let near = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        near = entry.isIntersecting;
        if (near) kick();
      },
      { rootMargin: '200px 0px' },
    );
    io.observe(sec);

    const onScroll = () => {
      if (near) kick();
    };
    const onResize = () => {
      geo = measure();
      kick();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    const ro = new ResizeObserver(onResize);
    ro.observe(l);
    document.fonts?.ready.then(() => {
      if (alive) onResize();
    });

    // first frame lands on the scroll position without easing
    val = target(geo);
    paint(geo, val);

    scrollToIndex.current = (i: number) => {
      const g = geo;
      const top = sec.getBoundingClientRect().top;
      window.scrollTo({
        top: window.scrollY + top - navh + (i / last) * g.range + 1,
        behavior: 'smooth',
      });
    };

    return () => {
      alive = false;
      io.disconnect();
      ro.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [
    refs.section,
    refs.pin,
    refs.listWrap,
    refs.list,
    refs.frame,
    count,
    navh,
  ]);

  return scrollToIndex;
}
